/**
 * Answer edits on ordinary (flat) questions: an answer renamed, hidden or moved. M4S-5777, X144 and
 * decision K3. Spec: docs/pdlc/crosstabs-classic-conversion.md §5.
 *
 * Until now the only home for these edits was `rowOverrides`, which the browser keeps
 * (`ct:layout:<key>`), so an edit followed neither the crosstab nor the person to another browser,
 * and a classic crosstab's renamed and hidden answers had nowhere to go. This module is the record's
 * home for them: `ai_layout.state.answerEdits`, a record setting (`RECORD_SETTING_KEYS`) and a
 * compute input like `matrixEdits`, so an edit waits for Run and reaches every export.
 *
 * Rules:
 *  - Edits name answers by survey code, never by label: a label is what an edit changes, and a
 *    code is what respondents' answers hold. An item's key is the answer's codes (`answerKey`), the
 *    key a Top 2 anchor already names a row by.
 *  - A question's edits are its answers in display order. An answer the edits do not list (one a
 *    later wave added) keeps its place after them, in survey order, shown and as surveyed.
 *  - Hide is display-only. Bases, nets, Top 2 / Bottom 2 and Mean still count a hidden answer, as
 *    they do for a hidden answer today (`listedOptions`).
 *  - The record wins. A browser's `rowOverrides` for a question is read only when the record holds
 *    no edits for it (`overrideFor`), so edits made before 3 September still show in the
 *    browser that made them. The one writer of `rowOverrides` left, the flat-question dialog
 *    (`handleSaveQuestionOverride`), is dormant: the flat pencil opens the Nets tab instead.
 *  - Clearing a question's edits (`withAnswerEdits(…, null)`) is Show as surveyed on the
 *    conversion notice or in the Edit question dialog (X54), which is where they are written
 *    (`question-edits.ts`); a matrix's dialog writes only its title here (`withQuestionTitle`).
 *  - M4S-5777 (X54): an entry may also hold the question's `title` and its box rows' names
 *    (`boxes`, by kind). Compute prints them; nothing decides a count by either.
 *  - Stored in one canonical shape (`parseAnswerEdits`): ai_layout is JSONB, which hands object keys
 *    back ordered by length, and the compute dirty check and the save signature compare JSON.
 *
 * Pure: no React, no fetch.
 */

import type {
    CrosstabQuestionDto,
    CrosstabQuestionOptionDto,
} from '@/lib/crosstab/question-formatter';

/** The longest label a rename keeps. The same bound as a matrix edit's. */
export const ANSWER_EDIT_LABEL_MAX = 500;
/** The most answers one question's edits may list. */
const LIST_MAX = 1000;

/** One answer's edit. Persisted in ai_layout.state.answerEdits. */
export interface AnswerItemEdit {
    /** The answer's survey code, upper-cased. */
    key: string;
    /** A rename. Absent means the survey's label. */
    label?: string;
    /** Hidden from the tables. Display-only: every count still includes it. */
    hidden?: true;
}

/**
 * M4S-5777 (X54): the box rows a question's Top 2 / Bottom 2 and NPS draw, by what they are
 * (`ScaleNetType`), never by their label. `nps` is the NPS Score row.
 */
export const BOX_LABEL_KEYS = [
    'top',
    'bottom',
    'promoters',
    'neutral',
    'detractors',
    'nps',
    // M4S-5777 (X79): the single Top Box, appended so stored edits keep their key order.
    'top1',
] as const;
export type BoxLabelKey = (typeof BOX_LABEL_KEYS)[number];

/** One question's edits: its answers in display order. */
export interface QuestionAnswerEdits {
    /** M4S-5777 (X54): the question's title as the tables and every export print it. */
    title?: string;
    answers: AnswerItemEdit[];
    /** M4S-5777 (X54): the question's box rows renamed, by kind. Only the renamed ones. */
    boxes?: Partial<Record<BoxLabelKey, string>>;
}

/** Upper-cased qid → edits. Only edited questions, keys sorted. */
export type AnswerEditsMap = Record<string, QuestionAnswerEdits>;

/** Browser-kept option lists, upper-cased qid → the options as the dialog saved them. */
export type RowOverridesMap = Record<string, CrosstabQuestionOptionDto[]>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return (
        typeof value === 'object' &&
        value !== null &&
        Object.prototype.toString.call(value) === '[object Object]'
    );
}

function parseLabel(raw: unknown): string | undefined {
    if (typeof raw !== 'string') return undefined;
    const label = raw.trim();
    return label && label.length <= ANSWER_EDIT_LABEL_MAX ? label : undefined;
}

/** The one builder of a stored item, in one field order. */
function item(key: string, label?: string, hidden?: boolean): AnswerItemEdit {
    const out: AnswerItemEdit = { key };
    if (label !== undefined) out.label = label;
    if (hidden) out.hidden = true;
    return out;
}

/**
 * Untrusted JSON (the record, a POST body, a browser slot) to one question's well-formed edits, or
 * null when it holds none. Never throws. Duplicate keys keep the first.
 */
export function parseQuestionAnswerEdits(raw: unknown): QuestionAnswerEdits | null {
    if (!isPlainObject(raw)) return null;
    const answers: AnswerItemEdit[] = [];
    const seen = new Set<string>();
    for (const entry of Array.isArray(raw.answers) ? raw.answers : []) {
        if (answers.length >= LIST_MAX) break;
        if (!isPlainObject(entry) || typeof entry.key !== 'string') continue;
        const key = entry.key.trim().toUpperCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        answers.push(item(key, parseLabel(entry.label), entry.hidden === true));
    }
    // M4S-5777 (X54): a title and box labels, each kept only when it is a real label.
    const title = parseLabel(raw.title);
    const boxes: Partial<Record<BoxLabelKey, string>> = {};
    if (isPlainObject(raw.boxes)) {
        for (const k of BOX_LABEL_KEYS) {
            const label = parseLabel(raw.boxes[k]);
            if (label) boxes[k] = label;
        }
    }
    const hasBoxes = Object.keys(boxes).length > 0;
    if (!answers.length && !title && !hasBoxes) return null;
    // One field order: title, answers, boxes (the dirty check and the save signature compare JSON).
    const out: QuestionAnswerEdits = title ? { title, answers } : { answers };
    if (hasBoxes) out.boxes = boxes;
    return out;
}

/**
 * M4S-5777 (X54): one question's title set (or cleared, when blank or as surveyed), the rest of its
 * edits kept. A matrix question's title rides here too: its option edits are `matrixEdits`.
 */
export function withQuestionTitle(
    map: AnswerEditsMap | undefined,
    qid: string,
    title: string,
    surveyed: string,
): AnswerEditsMap {
    const QID = qid.trim().toUpperCase();
    const current = parseAnswerEdits(map)[QID];
    const next: QuestionAnswerEdits = { answers: current?.answers ?? [] };
    const t = title.trim();
    if (t && t !== surveyed.trim()) next.title = t;
    if (current?.boxes) next.boxes = current.boxes;
    return withAnswerEdits(map, QID, next.title || next.answers.length || next.boxes ? next : null);
}

/** M4S-5777 (X54): the title a question's tables print: its rename, else as surveyed. */
export function questionTitleOf(
    q: Pick<CrosstabQuestionDto, 'qid' | 'question'>,
    answerEdits: AnswerEditsMap | undefined,
): string {
    return answerEdits?.[q.qid.toUpperCase()]?.title ?? q.question;
}

/** M4S-5777 (X54): a box row's label on a question: its rename, else `fallback`. */
export function boxLabelOf(
    qid: string,
    key: BoxLabelKey,
    answerEdits: AnswerEditsMap | undefined,
    fallback: string,
): string {
    return answerEdits?.[qid.toUpperCase()]?.boxes?.[key] ?? fallback;
}

/** Untrusted JSON to a well-formed map with its keys sorted. Never throws. */
export function parseAnswerEdits(raw: unknown): AnswerEditsMap {
    const found: AnswerEditsMap = {};
    if (!isPlainObject(raw)) return found;
    // Sorted first, so that of two keys naming one qid ("q1", "Q1") the same one always wins.
    for (const rawQid of Object.keys(raw).sort()) {
        const qid = rawQid.trim().toUpperCase();
        if (!qid || Object.prototype.hasOwnProperty.call(found, qid)) continue;
        const edits = parseQuestionAnswerEdits(raw[rawQid]);
        if (edits) found[qid] = edits;
    }
    const out: AnswerEditsMap = {};
    for (const qid of Object.keys(found).sort()) out[qid] = found[qid];
    return out;
}

/** Writes one question's edits; null deletes them. Upper-cases the qid and keeps the map canonical. */
export function withAnswerEdits(
    map: AnswerEditsMap | undefined,
    qid: string,
    edits: QuestionAnswerEdits | null,
): AnswerEditsMap {
    const QID = qid.trim().toUpperCase();
    const next: Record<string, unknown> = { ...parseAnswerEdits(map) };
    delete next[QID];
    if (QID && edits) next[QID] = edits;
    return parseAnswerEdits(next);
}

/** Merges `extra` into `map`: a question `extra` names takes `extra`'s edits. */
export function mergeAnswerEdits(
    map: AnswerEditsMap | undefined,
    extra: AnswerEditsMap | undefined,
): AnswerEditsMap {
    return parseAnswerEdits({ ...parseAnswerEdits(map), ...parseAnswerEdits(extra) });
}

/**
 * The key an answer is edited by: its codes, upper-cased, distinct, sorted and joined with `+`.
 * The same key `rowKey` gives a flat question's row in `box-rows.ts` (a Top 2 anchor names a row by
 * it), so an edit and an anchor name an answer alike; answer-edits.test.ts holds the two together.
 * A survey net that holds codes 1 to 9 is `1+2+…+9`, never confused with the answer `1`.
 */
export function answerKey(option: Pick<CrosstabQuestionOptionDto, 'option_id'>): string {
    const codes = (Array.isArray(option.option_id) ? option.option_id : [])
        .map((code) => String(code).trim().toUpperCase())
        .filter(Boolean);
    return [...new Set(codes)].sort().join('+');
}

/**
 * A question's options with its edits applied, in the shape compute and the rail already read
 * (`listedOptions`): renamed, `hidden` where hidden, in display order. An item names the option
 * whose key it is; a one-code key that no option has as its whole key finds the first answer
 * holding that code, so an edit keyed by one wave's code still finds a merged-wave answer. Options
 * the edits do not list follow, as surveyed. Undefined when there are no edits, or none of them
 * names an answer this question has, so the question draws as surveyed.
 */
export function answerEditsOverride(
    q: Pick<CrosstabQuestionDto, 'options'>,
    edits: QuestionAnswerEdits | null | undefined,
): CrosstabQuestionOptionDto[] | undefined {
    const order = answerEditsOrder(q, edits);
    if (!order) return undefined;
    return order.map(({ index, label, hidden }) => ({
        ...q.options[index],
        ...(label !== undefined ? { option: label } : {}),
        hidden,
    }));
}

/** One option of a question in display order: its index in `q.options`, its rename, and whether it is hidden. */
export interface AnswerOrderEntry {
    index: number;
    /** The key the edit names it by, when an edit does (it may be one wave's code). */
    key?: string;
    label?: string;
    hidden: boolean;
}

/**
 * M4S-5777 (X30, X31): where each of a question's options sits once its edits apply, by its index
 * in `q.options`. The one matching rule behind `answerEditsOverride` (the rows) and the banner's
 * columns (`computeFromApi`), so a hidden, moved or renamed answer is the same answer on both
 * sides. An item names the option whose key it is; a one-code key that no option has as its whole
 * key finds the first answer holding that code. Options the edits do not list follow, as surveyed.
 * Undefined when there are no edits, or none of them names an answer this question has.
 */
export function answerEditsOrder(
    q: Pick<CrosstabQuestionDto, 'options'>,
    edits: QuestionAnswerEdits | null | undefined,
): AnswerOrderEntry[] | undefined {
    if (!edits?.answers.length) return undefined;
    const byKey = new Map<string, number>();
    const byCode = new Map<string, number>();
    q.options.forEach((option, index) => {
        const key = answerKey(option);
        if (key && !byKey.has(key)) byKey.set(key, index);
        if (option.isNet) return;
        for (const id of Array.isArray(option.option_id) ? option.option_id : []) {
            const code = String(id).trim().toUpperCase();
            if (!byCode.has(code)) byCode.set(code, index);
        }
    });
    const used = new Set<number>();
    const out: AnswerOrderEntry[] = [];
    for (const edit of edits.answers) {
        const index =
            byKey.get(edit.key) ?? (edit.key.includes('+') ? undefined : byCode.get(edit.key));
        if (index === undefined || used.has(index)) continue;
        used.add(index);
        out.push({
            index,
            key: edit.key,
            ...(edit.label !== undefined ? { label: edit.label } : {}),
            hidden: edit.hidden === true,
        });
    }
    if (out.length === 0) return undefined;
    q.options.forEach((_, index) => {
        if (!used.has(index)) out.push({ index, hidden: false });
    });
    return out;
}

/**
 * The option list one flat question draws from: the record's answer edits when it holds any for the
 * question, else the browser's `rowOverrides` entry, else undefined (the question as surveyed).
 * Compute and the rail's Top 2 / Bottom 2 summary both call this, so they read the two sources in
 * one order and cannot disagree.
 */
export function overrideFor(
    q: Pick<CrosstabQuestionDto, 'qid' | 'options'>,
    answerEdits: AnswerEditsMap | undefined,
    rowOverrides: Readonly<RowOverridesMap> | undefined,
): CrosstabQuestionOptionDto[] | undefined {
    const QID = q.qid.toUpperCase();
    return answerEditsOverride(q, answerEdits?.[QID]) ?? rowOverrides?.[QID];
}
